import { isNode } from "reactflow"
import { ensureNumber, objectDiff } from "../utils/utils"
import { getAllActionItems } from "../utils/actionItem"
import { getProcessTaskBudget } from "../utils/financials"
import groupBy from "lodash/groupBy"
import { toZonedTime } from "date-fns-tz"
import { eachDayOfInterval } from "date-fns/eachDayOfInterval"
import { endOfDay } from "date-fns/endOfDay"
import addDays from "date-fns/addDays"
import { getTimeUtc, getStartHour } from "../utils/dates"
import { getSections, getSection, getTaskSectionId } from "../utils/sections"
import startOfDay from "date-fns/startOfDay"

import dagre from "dagre"
import isEqual from "lodash/isEqual"

const updateGhosts = ({ app, mission, changes, marker, newCol, allAis }) => {
    let changesNeeded = []
    const oldColIndex = mission.cols.findIndex((c) => c.id === marker.col)
    const newColIndex = mission.cols.findIndex((c) => c.id === newCol.id)
    const oldCol = mission.cols[oldColIndex]
    const oldTask = allAis.find((a) => a._id === oldCol?.linkedToProcess)
    const newTask = allAis.find((a) => a._id === newCol.linkedToProcess)

    if (oldTask) {
        let isDone = {}
        let isOpen = {}

        if (oldColIndex < newColIndex) {
            isDone = {
                completedBy: app.state.person._id,
                completedOn: getTimeUtc(new Date()),
                status: "done",
            }
        } else {
            isOpen = {
                completedBy: null,
                completedOn: null,
                status: "open",
                endDate: getTimeUtc(endOfDay(addDays(new Date(), (oldTask.estimatedDuration || 8) / 8))),
            }
        }

        changesNeeded.push({
            obj: oldTask,
            change: {
                _id: oldTask._id,
                ...isDone,
                ...isOpen,
            },
        })
    }
    if (newTask) {
        changesNeeded.push({
            obj: newTask,
            change: {
                _id: newTask._id,
                status: "open",
                week: marker.week,
                startDate: newTask.startDate || getTimeUtc(startOfDay(new Date())),
                startHour: getStartHour(new Date()),
                endDate: getTimeUtc(endOfDay(addDays(new Date(), (newTask.estimatedDuration || 8) / 8))),
            },
        })
    }

    if (changesNeeded.length) {
        setTimeout(() => {
            try {
                app.actionItemUpdateBulk(mission._id, changesNeeded, false, (dd) => {})
            } catch (er) {
                app.silentFail("wpeoi93mm-TakKanban" + er)
            } finally {
            }
        }, 500)
    }
}

export const processMoveLogic = ({ col, marker, changes, app, mission }) => {
    const allAis = getAllActionItems(mission)
    const ai = allAis.find((a) => a._id === col.linkedToProcess)

    if (!ai) {
        app.silentFail("w2o3u2398u-00")
        return
    }

    updateGhosts({ changes, newCol: col, marker, allAis, mission, app })

    if (col.id === "todo" || col.id === "done") {
        if (ai.people?.length) {
            app.actionItemRemovePersonAll(ai._id)
        }
        return
    }

    const planItems = mission.planItems.filter((p) => {
        return p.raciOnTasks?.find((rp) => rp.taskId === ai._id)
    })

    if (ai.people?.length) {
        app.actionItemRemovePersonAll(ai._id, () => {
            planItems.forEach((p, i) => {
                app.actionItemAddPerson(ai, p.person)
            })
        })
    } else {
        planItems.forEach((p, i) => {
            app.actionItemAddPerson(ai, p.person)
        })
    }
}

export const sortSteps = ({ edges, nodes }) => {
    return nodes.sort((a, b) => {
        // Sort by the 'y' value (relative coordinate) in ascending order
        const yDiff = a.position.y - b.position.y

        // If y values are equal, maintain original order (stable sort)
        // Alternatively, you could add a secondary sort like a.x - b.x
        return yDiff
    })
}

export function syncProcessFlow({ mission, app, cb }) {
    const allAi = getAllActionItems(mission)

    const fd = mission.flowData ? JSON.parse(mission.flowData) : { edges: [], nodes: [] }

    let deleteAis = []

    let newNodes = (fd.nodes || []).slice()
    let newEdges = (fd.edges || []).slice().filter((ed) => ed.data)
    const sections = getSections(mission)
    let newSections = sections.slice()
    let deleteSections = []

    sections.forEach((st, i) => {
        if (!fd.nodes.find((n) => n.id === st._id)) {
            newSections.splice(1, i)

            deleteSections.push(st)
            //Delete tasks and nodes and egdes related to st
            newNodes = newNodes.filter((n) => n.data?.sectionTagId !== st._id)

            newEdges = newEdges.filter(
                (n) => !n.data || (n.data?.sourceSectionTagId !== st._id && n.data?.targetSectionTagId !== st._id)
            )
        }
    })

    deleteAis = allAi.filter((ai) => !newNodes.find((n) => n.id === ai._id))

    let newAis = allAi.filter((ai) => !deleteAis.find((a) => a._id === ai._id))

    let cols = [{ name: "Todo", id: "todo", source: "ai", people: [], linkedToProcess: undefined, color: undefined }]
    const nodes = sortSteps({ edges: newEdges, nodes: newNodes })
    nodes.forEach((n, i) => {
        const ai = newAis.find((a) => a._id === n.id)

        if (ai) {
            const st = newSections.find((s) => s._id === n.data?.sectionTagId)
            if (st)
                cols.push({
                    name: ai.title,
                    id: ai._id,
                    source: app.state.person._id,
                    people: ai.people,
                    linkedToProcess: ai._id,
                    color: st.color,
                })
        }
    })

    cols.push({ name: "Done", id: "done", source: "ai", people: [], linkedToProcess: undefined, color: undefined })

    if (deleteAis.length) {
        app.actionItemDeleteBulk(deleteAis)
    }
    const mc = mission.cols.map((c) => ({
        name: c.name,
        id: c.id,
        source: c.source,
        people: c.people,
        linkedToProcess: c.linkedToProcess,
        color: c.color,
    }))

    let changeObj = {}

    if (!isEqual(cols, mc)) {
        changeObj.cols = cols
    }
    if (newEdges.length !== fd.edges.length || newNodes.length !== fd.nodes.length) {
        changeObj.flowData = JSON.stringify({ nodes: newNodes, edges: newEdges })
    }
    if (deleteSections.length) {
        deleteSections.forEach((st, i) => {
            app.missionDeleteSection(mission._id, st)
        })
    }

    if (Object.keys(changeObj).length) {
        app.missionUpdate(mission._id, changeObj, (res) => {})
    }

    let stAi = []

    mission.planItems.forEach((p, i) => {
        p.raciOnTasks?.forEach((rt, i) => {
            if (rt.hoursNeeded) {
                const ogAi = newAis.find((ai) => ai._id === rt.taskId)

                if (ogAi) {
                    stAi.push({ ai: ogAi, hoursNeeded: rt.hoursNeeded, sectionId: getTaskSectionId(mission, ogAi) })
                }
            }
        })
    })
    let stChangesNeeded = []
    if (stAi.length) {
        const gAi = groupBy(stAi, "sectionId")

        Object.keys(gAi).forEach((key, i) => {
            const st = getSection(mission, key)

            // Steps in no section have no dates to plan
            if (!st) return

            const days =
                roundToMultiplier(
                    gAi[key].reduce((cumm, obj) => (cumm += ensureNumber(obj.hoursNeeded)), 0),
                    8
                ) / 8

            const daysOfSt =
                st.from && st.to
                    ? eachDayOfInterval({
                          start: toZonedTime(st.from),
                          end: toZonedTime(st.to),
                      }).length
                    : 0

            if (ensureNumber(daysOfSt) !== ensureNumber(days)) {
                const newEndDate = endOfDay(addDays(toZonedTime(mission.planStartDate), days - 1))

                stChangesNeeded.push({
                    st: st,
                    from: mission.planStartDate,
                    to: getTimeUtc(newEndDate),
                })
            }
        })
    }

    stChangesNeeded.forEach((stc, i) => {
        app.missionUpdateSection(mission, stc.st, {
            from: stc.from,
            to: stc.to,
        })
    })
}

export function getMaxMinDataOfProcess({ mission, nodes, edges }) {
    if (!mission.flowData) {
        return {
            minHours: 0,
            maxHours: 0,
            minCost: 0,
            minRev: 0,
            maxCost: 0,
            maxRev: 0,
        }
    }

    if (!nodes || !edges) {
        const flow = JSON.parse(mission.flowData)

        if (!nodes) {
            nodes = flow.nodes
        }
        if (!edges) {
            edges = flow.edges
        }
    }

    if (!nodes.length) {
        return {
            minHours: 0,
            maxHours: 0,
            minCost: 0,
            minRev: 0,
            maxCost: 0,
            maxRev: 0,
        }
    }

    const allActionItems = Object.fromEntries(getAllActionItems(mission).map((item) => [item._id, item]))

    const exploreAllPaths = (startId) => {
        let maxTotal = -Infinity
        let minTotal = Infinity

        let minCostTotal = Infinity
        let maxCostTotal = -Infinity
        let minRevTotal = Infinity
        let maxRevTotal = -Infinity

        function dfs(nodeId, currentTotal, currentCostTotal, currentRevTotal, visited) {
            if (visited.has(nodeId)) return

            const node = nodes.find((n) => n.id === nodeId)
            if (!node) return

            visited.add(nodeId)

            const ai = allActionItems[nodeId]
            // Accumulate weight

            if (ai) {
                currentTotal += ai.estimatedDuration || 8
                const budget = getProcessTaskBudget({ ai, mission })
                currentCostTotal += budget.estCost
                currentRevTotal += budget.estRevenue
            }

            const outgoingEdges = edges.filter((e) => e.source === nodeId && !visited.has(e.target))

            if (outgoingEdges.length === 0) {
                // If no more paths, update max and min totals
                maxTotal = Math.max(maxTotal, currentTotal)
                minTotal = Math.min(minTotal, currentTotal)

                maxCostTotal = Math.max(maxCostTotal, currentCostTotal)
                minCostTotal = Math.min(minCostTotal, currentCostTotal)

                maxRevTotal = Math.max(maxRevTotal, currentRevTotal)
                minRevTotal = Math.min(minRevTotal, currentRevTotal)
            } else {
                outgoingEdges.forEach((edge) => {
                    dfs(edge.target, currentTotal, currentCostTotal, currentRevTotal, new Set(visited)) // Continue DFS with the accumulated total
                })
            }
        }

        if (nodes?.filter((n) => n.type === "flowNode").length === 1) {
            const ai = allActionItems[nodes.find((n) => n.type === "flowNode")?.id]
            // Accumulate weight

            if (ai) {
                maxTotal = ai.estimatedDuration || 8
                minTotal = 0
                minCostTotal = 0
                minRevTotal = 0
                const budget = getProcessTaskBudget({ ai, mission })
                maxCostTotal = budget.estCost
                maxRevTotal = budget.estRevenue
            }
        } else {
            dfs(startId, 0, 0, 0, new Set()) // Initialize currentTotal to 0
        }

        return { maxTotal, minTotal, maxCostTotal, minCostTotal, maxRevTotal, minRevTotal }
    }

    // Calculate totals from node 1 for demonstration
    const { maxTotal, minTotal, maxCostTotal, minCostTotal, maxRevTotal, minRevTotal } = exploreAllPaths(
        nodes.find((n) => n.type === "startNode").id
    )

    return {
        minHours: minTotal,
        maxHours: maxTotal,
        minCost: minCostTotal,
        minRev: minRevTotal,
        maxCost: maxCostTotal,
        maxRev: maxRevTotal,
    }
}
export const getOrphans = (nodes, edges) => {
    return nodes.filter((n) => n.type === "flowNode" && !edges.find((ed) => ed.target === n.id))
}

export function getIncomers(node, nodes, edges) {
    if (!isNode(node)) {
        return []
    }

    const incomersIds = edges.filter((e) => e.target === node.id).map((e) => e.source)

    return nodes.filter((e) =>
        incomersIds
            .map((id) => {
                const matches = /([\w-^]+)__([\w-]+)/.exec(id)
                if (matches === null) {
                    return id
                }
                return matches[1]
            })
            .includes(e.id)
    )
}

export function getOutgoers(node, nodes, edges) {
    if (!isNode(node)) {
        return []
    }

    const outgoerIds = edges.filter((e) => e.source === node.id).map((e) => e.target)

    return nodes.filter((n) =>
        outgoerIds
            .map((id) => {
                const matches = /([\w-^]+)__([\w-]+)/.exec(id)
                if (matches === null) {
                    return id
                }
                return matches[1]
            })
            .includes(n.id)
    )
}

const roundToMultiplier = (num, multiplier) => Math.ceil(num / multiplier) * multiplier

export const getDownstreamNodes = (startNode, edges, nodes) => {
    // Create a map of node ids to node objects for quick lookup
    const nodeMap = new Map(nodes.map((node) => [node.id, node]))

    // Create a map of edges from source node id to destination node id
    const adjacencyList = new Map()
    edges.forEach((edge) => {
        if (!adjacencyList.has(edge.source)) {
            adjacencyList.set(edge.source, [])
        }
        adjacencyList.get(edge.source).push(edge.target)
    })

    // Function to perform DFS and collect downstream nodes
    function dfs(nodeId, visited, currentPath) {
        if (!visited.has(nodeId)) {
            visited.add(nodeId)
            currentPath.add(nodeId)
            const neighbors = adjacencyList.get(nodeId) || []
            neighbors.forEach((neighbor) => {
                if (!currentPath.has(neighbor)) {
                    dfs(neighbor, visited, currentPath)
                }
            })
            currentPath.delete(nodeId)
        }
    }

    // Set to keep track of visited nodes
    const visited = new Set()
    // Set to keep track of the current path
    const currentPath = new Set()
    // Start DFS from the startNode
    dfs(startNode.id, visited, currentPath)

    // Return the list of downstream nodes including the original node
    return Array.from(visited).map((nodeId) => nodeMap.get(nodeId))
}
